import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import Navbar from "src/components/Navbar";
import Footer from "src/components/Footer";
import { Button } from "src/components/ui/Button";
import { SubjectSelector } from "src/components/assessment/SubjectSelector";
import { MathParamSelector } from "src/components/assessment/MathParamSelector";
import { CsTrackSelector } from "src/components/assessment/CsTrackSelector";
import { GuestIdentityForm } from "src/components/assessment/GuestIdentityForm";
import { CaptchaGate } from "src/components/assessment/CaptchaGate";
import { CS_COURSES } from "src/config/diagnosticCourses";
import { startDiagnostic } from "src/services/diagnosticService";
import useScrollToTop from "src/hooks/useScrollToTop";
import { cn } from "src/lib/utils";

type Step = 1 | 2 | 3 | 4;

interface FormErrors {
  name?: string;
  email?: string;
}

const STEPS: { id: Step; label: string }[] = [
  { id: 1, label: "Subject" },
  { id: 2, label: "Level" },
  { id: 3, label: "Name" },
  { id: 4, label: "Start" },
];

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function labelFor(options: { id: string; label: string }[], id: string | null) {
  return options.find((option) => option.id === id)?.label ?? "";
}

export default function DiagnosticSetup() {
  const navigate = useNavigate();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const skipInitialFocus = useRef(true);

  const [step, setStep] = useState<Step>(1);
  const [subject, setSubject] = useState<"math" | "cs" | null>(null);
  const [gradeId, setGradeId] = useState<string | null>(null);
  const [gradeLabel, setGradeLabel] = useState("");
  const [boardId, setBoardId] = useState<string | null>(null);
  const [boardLabel, setBoardLabel] = useState("");
  const [csCourse, setCsCourse] = useState<string | null>(null);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [identityErrors, setIdentityErrors] = useState<FormErrors>({});
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useScrollToTop();

  useEffect(() => {
    if (step !== 4) setCaptchaToken(null);
  }, [step]);

  useEffect(() => {
    setSubmitError(null);
  }, [step, gradeId, boardId, subject, csCourse]);

  useEffect(() => {
    if (skipInitialFocus.current) {
      skipInitialFocus.current = false;
      return;
    }
    headingRef.current?.focus({ preventScroll: true });
    if (window.matchMedia("(max-width: 1023px)").matches) {
      headingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [step]);

  const canAdvanceStep1 = subject !== null;
  const canAdvanceStep2 =
    subject === "math"
      ? gradeId !== null && boardId !== null
      : subject === "cs"
      ? csCourse !== null
      : false;

  const validateStep3 = (): boolean => {
    const errors: FormErrors = {};
    if (!guestName.trim()) errors.name = "Enter your name.";
    if (!guestEmail.trim()) {
      errors.email = "Enter an email address.";
    } else if (!isValidEmail(guestEmail)) {
      errors.email = "That email looks incomplete. Include the name and the domain.";
    }
    setIdentityErrors(errors);
    if (errors.name) {
      document.getElementById("guest-name")?.focus();
    } else if (errors.email) {
      document.getElementById("guest-email")?.focus();
    }
    return Object.keys(errors).length === 0;
  };

  const handleContinue = () => {
    setSubmitError(null);
    if (step === 1 && canAdvanceStep1) setStep(2);
    if (step === 2 && canAdvanceStep2) setStep(3);
    if (step === 3 && validateStep3()) setStep(4);
  };

  const handleBack = () => {
    setSubmitError(null);
    if (step > 1) setStep((step - 1) as Step);
  };

  const handleSubmit = async () => {
    if (!subject || !captchaToken) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const result = await startDiagnostic({
        subject,
        gradeId: subject === "math" ? gradeId : undefined,
        boardId: subject === "math" ? boardId : undefined,
        csCourse: subject === "cs" ? csCourse : undefined,
        guestName: guestName.trim(),
        guestEmail: guestEmail.trim(),
        captchaToken,
      });
      sessionStorage.setItem(`diag_token_${result.attemptId}`, result.attemptToken);
      navigate(`/diagnostic-assessment/${result.attemptId}`);
    } catch (err) {
      setCaptchaToken(null);
      setCaptchaReset((value) => value + 1);
      setSubmitError(
        err instanceof Error ? err.message : "The quiz did not start. Try again in a moment."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepTitle: Record<Step, string> = {
    1: "Mathematics or computer science",
    2: subject === "math" ? "Grade and board" : "Python or JavaScript",
    3: "Name and email",
    4: "Review, then start",
  };

  const stepDescription: Record<Step, string> = {
    1: "The quiz follows the subject you pick.",
    2:
      subject === "math"
        ? "Questions are written for that grade and curriculum."
        : "The quiz stays on one track.",
    3: "We send the report to this address.",
    4: "Check the details. The timer starts when the quiz opens.",
  };

  const canAdvance: Record<Step, boolean> = {
    1: canAdvanceStep1,
    2: canAdvanceStep2,
    3: true,
    4: !!captchaToken,
  };

  const summary = [
    ["Subject", subject === "math" ? "Mathematics" : subject === "cs" ? "Computer Science" : ""],
    subject === "math" ? ["Grade", gradeLabel] : ["Track", labelFor(CS_COURSES, csCourse)],
    ...(subject === "math" ? [["Board", boardLabel] as [string, string]] : []),
    ["Name", guestName.trim()],
    ["Email", guestEmail.trim()],
  ];

  return (
    <div className="flex min-h-screen flex-col bg-white selection:bg-rfpurple-200 selection:text-[#3e1168]">
      <Navbar />

      <main className="flex-grow py-6 sm:py-8 lg:py-12">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm lg:grid lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)]">
            <aside className="border-b border-gray-200 bg-purple-50/70 px-6 py-8 sm:px-8 sm:py-10 lg:border-b-0 lg:border-r lg:px-10 lg:py-12">
              <h1 className="text-balance font-roboto text-3xl font-extrabold leading-[1.05] tracking-[-0.035em] text-[#3e1168] sm:text-4xl">
                Diagnostic Assessment
              </h1>
              <p className="mt-4 max-w-prose text-base leading-relaxed text-rfpurple-800">
                Pinpoint learning gaps. Strengthen core skills. About 20 minutes, with the report sent by email. No account required.
              </p>

              <nav aria-label="Setup progress" className="mt-8 lg:mt-10">
                <ol className="flex flex-col gap-1">
                  {STEPS.map((item) => {
                    const done = step > item.id;
                    const active = step === item.id;
                    const className = cn(
                      "flex w-full items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-left text-sm",
                      active && "bg-white font-medium text-[#3e1168]",
                      done && "text-rfpurple-800 hover:bg-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      !active && !done && "text-rfpurple-700"
                    );
                    const marker = (
                      <span
                        className={cn(
                          "h-1.5 w-1.5 shrink-0 rounded-full",
                          active ? "bg-[#ff7f52]" : done ? "bg-rfpurple-600" : "bg-rfpurple-300"
                        )}
                        aria-hidden="true"
                      />
                    );

                    return (
                      <li key={item.id}>
                        {done ? (
                          <button
                            type="button"
                            onClick={() => {
                              setSubmitError(null);
                              setStep(item.id);
                            }}
                            className={className}
                          >
                            {marker}
                            {item.label}
                          </button>
                        ) : (
                          <span className={className} aria-current={active ? "step" : undefined}>
                            {marker}
                            {item.label}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </nav>
            </aside>

            <section
              className="px-6 py-8 caret-[#3e1168] sm:px-8 sm:py-10 lg:px-10 lg:py-12"
              aria-labelledby="diagnostic-step-title"
            >
              <div className="mb-6">
                <h2
                  id="diagnostic-step-title"
                  ref={headingRef}
                  tabIndex={-1}
                  className="scroll-mt-6 font-roboto text-xl font-bold tracking-[-0.02em] text-[#3e1168] outline-none sm:text-2xl"
                >
                  {stepTitle[step]}
                </h2>
                <p className="mt-2 max-w-prose text-sm leading-relaxed text-gray-600 sm:text-base">
                  {stepDescription[step]}
                </p>
              </div>

              {step === 1 && (
                <SubjectSelector
                  value={subject}
                  onChange={(next) => {
                    if (next !== subject) {
                      setGradeId(null);
                      setGradeLabel("");
                      setBoardId(null);
                      setBoardLabel("");
                      setCsCourse(null);
                    }
                    setSubject(next);
                  }}
                />
              )}

              {step === 2 && subject === "math" && (
                <MathParamSelector
                  gradeId={gradeId}
                  boardId={boardId}
                  onGradeChange={(id, label) => {
                    setGradeId(id);
                    setGradeLabel(label);
                    setBoardId(null);
                    setBoardLabel("");
                  }}
                  onBoardChange={(id, label) => {
                    setBoardId(id);
                    setBoardLabel(label);
                  }}
                />
              )}

              {step === 2 && subject === "cs" && (
                <CsTrackSelector value={csCourse} onChange={setCsCourse} />
              )}

              {step === 3 && (
                <GuestIdentityForm
                  name={guestName}
                  email={guestEmail}
                  onNameChange={(value) => {
                    setGuestName(value);
                    if (identityErrors.name) setIdentityErrors((errors) => ({ ...errors, name: undefined }));
                  }}
                  onEmailChange={(value) => {
                    setGuestEmail(value);
                    if (identityErrors.email) setIdentityErrors((errors) => ({ ...errors, email: undefined }));
                  }}
                  errors={identityErrors}
                />
              )}

              {step === 4 && (
                <div className="flex flex-col gap-6">
                  <dl className="border-y border-rfpurple-100">
                    {summary.map(([label, value]) => (
                      <div key={label} className="flex items-baseline justify-between gap-4 border-b border-rfpurple-100 py-2.5 last:border-b-0">
                        <dt className="text-sm text-gray-600">{label}</dt>
                        <dd className="max-w-[65%] text-right text-sm font-medium text-[#3e1168]">{value}</dd>
                      </div>
                    ))}
                  </dl>

                  <CaptchaGate
                    resetSignal={captchaReset}
                    onVerified={(token) => setCaptchaToken(token)}
                    onReset={() => setCaptchaToken(null)}
                  />

                  {submitError && (
                    <div className="flex items-start gap-2 rounded-md bg-red-50 p-4 text-sm text-red-700" role="alert">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                      <span>{submitError}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-8 flex items-center justify-between gap-3">
                {step > 1 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleBack}
                    disabled={isSubmitting}
                    className="text-[#3e1168] hover:bg-rfpurple-50 hover:text-[#3e1168]"
                  >
                    <ArrowLeft aria-hidden="true" />
                    Back
                  </Button>
                ) : (
                  <span />
                )}

                {step < 4 ? (
                  <Button
                    type="button"
                    onClick={handleContinue}
                    disabled={!canAdvance[step]}
                    className="button-gradient text-white"
                  >
                    Continue
                    <ArrowRight aria-hidden="true" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!captchaToken || isSubmitting}
                    className="button-gradient text-white"
                  >
                    {isSubmitting && <Loader2 className="animate-spin" aria-hidden="true" />}
                    {isSubmitting ? "Starting..." : "Start assessment"}
                  </Button>
                )}
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
